import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { launcher, type LauncherQuick, type SettingsScreen } from "./launcherApi.js";
import { networkLabel, storageLabel } from "./launcherTiles.js";

/**
 * Quick settings, the launcher's first tile (owner, 2026-10-20: "have quick settings before
 * android settings"). L1: what the box is and how it is connected, volume, and the screens
 * this box can open; full Bluetooth and Wi-Fi control follows in L2 (playbook §6a).
 *
 * A popup like every other: on top, focus inside, Back closes it and focus returns to the tile
 * that opened it. Rows of buttons; Up and Down move between rows, Left and Right along one.
 */
export function QuickSettings({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [quick, setQuick] = useState<LauncherQuick | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useDialogFocus({ open, onClose, rootRef: dialogRef });

  useEffect(() => {
    if (!open) return;
    setNote(null);
    let live = true;
    void launcher.quick().then((answer) => {
      if (live) setQuick(answer);
    }).catch(() => {
      if (live) setNote("This box did not answer. Try again in a moment.");
    });
    return () => {
      live = false;
    };
  }, [open]);

  // Focus the first button once the readings are in.
  useEffect(() => {
    if (open && quick) dialogRef.current?.querySelector<HTMLButtonElement>("[data-qs-row='0'][data-qs-col='0']")?.focus();
  }, [open, quick]);

  if (!open) return null;

  const openScreen = (key: SettingsScreen) => {
    void launcher.openSettings(key).then(onClose).catch(() => setNote("This box has no such screen."));
  };
  const changeVolume = (delta: number) => {
    if (!quick) return;
    void launcher.setVolume(quick.sound.volume + delta).then((sound) => setQuick({ ...quick, sound })).catch(() => setNote("This box's volume is fixed."));
  };
  const toggleMute = () => {
    if (!quick) return;
    void launcher.toggleMute().then((sound) => setQuick({ ...quick, sound })).catch(() => setNote("This box's volume is fixed."));
  };

  const rows: Array<{ label: string; value: string; buttons: Array<{ text: string; action: () => void; aria?: string }> }> = [];
  if (quick) {
    rows.push({
      label: "Network",
      // The address on its own line: on one line it was cut off (onn box, 2026-10-20).
      value: `${networkLabel(quick)}${quick.address ? `
${quick.address}` : ""}`,
      buttons: quick.screens.network ? [{ text: "Network & Wi-Fi", action: () => openScreen("network") }] : [],
    });
    rows.push({
      label: "Bluetooth",
      value: !quick.bluetoothPresent ? "Not on this box" : quick.bluetoothOn ? "On" : "Off",
      buttons: quick.screens.accessories ? [{ text: "Remotes & accessories", action: () => openScreen("accessories") }] : [],
    });
    rows.push({
      label: "Volume",
      value: quick.sound.fixed ? "Set by the TV" : `${quick.sound.volume} of ${quick.sound.max}`,
      buttons: quick.sound.fixed
        ? []
        : [
            { text: "−", action: () => changeVolume(-1), aria: "Volume down" },
            { text: "+", action: () => changeVolume(1), aria: "Volume up" },
            { text: "Mute", action: toggleMute, aria: "Mute or unmute" },
          ],
    });
    rows.push({
      label: "Storage",
      value: storageLabel(quick.storage.freeBytes, quick.storage.totalBytes),
      buttons: quick.screens.storage ? [{ text: "Storage", action: () => openScreen("storage") }] : [],
    });
    if (quick.screens.displaysound) {
      rows.push({ label: "Picture & sound", value: "", buttons: [{ text: "Display & sound", action: () => openScreen("displaysound") }] });
    }
    if (quick.screens.all) {
      rows.push({ label: "Everything else", value: "", buttons: [{ text: "All Android settings", action: () => openScreen("all") }] });
    }
  }
  // Rows with buttons are the ones focus can reach; numbered in order for the arrow keys.
  let focusRow = 0;
  const numbered = rows.map((row) => ({ ...row, index: row.buttons.length > 0 ? focusRow++ : -1 }));

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const active = document.activeElement as HTMLElement | null;
    const row = Number(active?.dataset.qsRow ?? -1);
    const col = Number(active?.dataset.qsCol ?? 0);
    if (row < 0) return;
    let target: HTMLElement | null = null;
    const at = (r: number, c: number) => dialogRef.current?.querySelector<HTMLElement>(`[data-qs-row='${r}'][data-qs-col='${c}']`) ?? null;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      const next = row + (event.key === "ArrowDown" ? 1 : -1);
      if (next < 0 || next >= focusRow) {
        event.preventDefault();
        return;
      }
      // The same column, or the last button of a shorter row.
      for (let c = col; c >= 0 && !target; c--) target = at(next, c);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      target = at(row, col + (event.key === "ArrowRight" ? 1 : -1));
      if (!target) {
        event.preventDefault();
        return;
      }
    } else {
      return;
    }
    if (target) {
      target.focus();
      event.preventDefault();
    }
  };

  return createPortal(
    <div className="epg-program-detail-overlay exit-confirm-overlay">
      <div
        ref={dialogRef}
        className="epg-program-detail quick-settings"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-settings-title"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <h2 className="epg-detail-title" id="quick-settings-title">Quick settings</h2>
        {quick && <p className="quick-settings__box">{quick.boxName}  ·  Android {quick.android}</p>}
        {!quick && !note && <p className="quick-settings__box">Reading this box…</p>}
        <div className="quick-settings__rows">
          {numbered.map((row) => (
            <div key={row.label} className="quick-settings__row">
              <span className="quick-settings__label">{row.label}</span>
              <span className="quick-settings__value">{row.value}</span>
              <span className="quick-settings__buttons">
                {row.buttons.map((button, col) => (
                  <button
                    key={button.text}
                    type="button"
                    className="ui-btn quick-settings__btn"
                    data-qs-row={row.index}
                    data-qs-col={col}
                    aria-label={button.aria}
                    onClick={button.action}
                  >
                    {button.text}
                  </button>
                ))}
              </span>
            </div>
          ))}
        </div>
        {note && <p className="quick-settings__note" role="status">{note}</p>}
        <p className="quick-settings__hint">Back closes this panel.</p>
      </div>
    </div>,
    document.body,
  );
}
